/*
  Lazily load video.js stuff from main.js when it's detected to be needed.
*/

import { findAll, whenSelectorPresentAll } from '../../../common/helpers/dom'
import { sizeWrapper } from './wrapper'
import './placeholder.scss'
import { onMobileAppChromecastLoadedIfAvailable } from './custom_plugins/mobile_app_chromecast'

const isInHiddenModal = (element) => {
  const modal = element.closest('.t-modal')
  if (!modal) return false

  return modal.getAttribute('aria-hidden') !== 'false'
}

const initializeVideojs = async () => {
  const { initializeVideoPlayer } = await import('./main.js')

  onMobileAppChromecastLoadedIfAvailable(() => {
    findAll('video:not(.no-video-js):not(.video-js--initialized)').forEach(
      async (video) => {
        // Skip videos inside hidden modals - they'll be initialized when the modal opens
        if (isInHiddenModal(video)) return

        initializeVideoPlayer(video)
      }
    )
  })
}

const initializeAudiojs = async () => {
  const { initializeAudioPlayer } = await import('./main.js')
  findAll(
    'audio:not(.jp-audio):not(.no-video-js):not(.video-js--initialized)'
  ).forEach(async (audio) => {
    // Skip audio inside hidden modals - they'll be initialized when the modal opens
    if (isInHiddenModal(audio)) return

    initializeAudioPlayer(audio)
  })
}

let ranInitializationAlready = false
window.initializeAllVideojs = () => {
  if (ranInitializationAlready) return
  ranInitializationAlready = true
  initializeVideojs()
  initializeAudiojs()
}

// If we're somewhere with turbolinks, navigation should reset this flag so that more videos can be initialized.
document.addEventListener('page:change', () => {
  ranInitializationAlready = false
})

// Initialize videos and audios in modals when they are shown
document.addEventListener('modal:shown', (e) => {
  const modal = e.target
  if (!modal.classList.contains('t-modal')) return

  const videos = modal.querySelectorAll(
    'video:not(.no-video-js):not(.video-js--initialized)'
  )
  const audios = modal.querySelectorAll(
    'audio:not(.jp-audio):not(.no-video-js):not(.video-js--initialized)'
  )

  if (videos.length > 0) initializeVideojs()
  if (audios.length > 0) initializeAudiojs()
})

// Initialize if it's added after load.
if (window.onDomChanged) {
  window.onDomChanged('video', initializeVideojs)
  window.onDomChanged('audio', initializeAudiojs)
}

// As soon as one video.js is on screen, we'll initialize them all. We also need to watch out for dynamic adds by
// the site builder. Calling initializeAllVideojs multiple times is fine as it's a no-op if the video has already been
// initialized.
whenSelectorPresentAll(
  'video:not(.no-video-js):not(.video-js--initialized), audio:not(.jp-audio):not(.no-video-js):not(.video-js--initialized)',
  (el) => {
    // Skip audios and videos inside hidden modals - they'll be initialized when the modal opens
    if (isInHiddenModal(el)) return

    if ('IntersectionObserver' in window) {
      let observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            window.initializeAllVideojs()
          }
        },
        { rootMargin: '400px' }
      )

      observer.observe(el)
      // Prevent a different sizing prior to video.js being loaded.
      sizeWrapper(el)
    } else {
      // Fall back, initialize now
      window.initializeAllVideojs()
    }
  }
)
